import Marquee from 'react-fast-marquee';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Container, Section, Overline, H2, Lead, FadeUp, Counter } from './shared';
import { TRUSTED, METRICS, PAIN_POINTS, MODULES, STEPS } from './landingData';

export const LogoRibbon = () => (
  <section className="py-10 border-y border-white/[0.06] overflow-hidden" data-testid="social-proof-bar">
    <Container>
      <p className="ld-overline justify-center mb-6 !text-slate-500">Trusted by 500+ high-growth teams across India</p>
      <div className="ld-ribbon-fade">
        <Marquee speed={35} gradient={false} pauseOnHover>
          {TRUSTED.map((c) => <span key={c} className="ld-display mx-10 text-lg font-semibold tracking-wide text-slate-600 hover:text-slate-300 transition-colors">{c}</span>)}
        </Marquee>
      </div>
    </Container>
  </section>
);

export const MetricsBand = () => (
  <section className="py-16" data-testid="metrics-section">
    <Container>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]">
        {METRICS.map((m, i) => (
          <FadeUp key={m.label} delay={i * 0.08} className="bg-[#0B0C0E] p-6 sm:p-8">
            <p className="ld-display text-3xl sm:text-4xl font-bold text-slate-50" data-testid={`metric-value-${i}`}><Counter {...m} /></p>
            <p className="text-sm text-slate-400 mt-2 leading-snug">{m.label}</p>
          </FadeUp>
        ))}
      </div>
    </Container>
  </section>
);

export const PainPoints = () => (
  <Section testId="problem-section">
    <Container>
      <FadeUp className="max-w-2xl">
        <Overline>The problem</Overline>
        <H2 data-testid="problem-heading">Operations break where your tools stop talking to each other.</H2>
      </FadeUp>
      <div className="grid md:grid-cols-3 gap-4 mt-12">
        {PAIN_POINTS.map((p, i) => (
          <FadeUp key={p.title} delay={i * 0.1}>
            <div className="ld-card ld-card-lift p-6 sm:p-8 h-full" data-testid={`pain-card-${i}`}>
              <AlertTriangle className="w-5 h-5 text-[#E06D53] mb-5" />
              <h3 className="text-lg font-semibold text-slate-100">{p.title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{p.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.3} className="mt-10 flex items-center gap-2 text-sm text-[#E06D53] font-medium">Woxora fixes all of this <ArrowRight className="w-4 h-4" /></FadeUp>
    </Container>
  </Section>
);

export const ModulesBento = () => (
  <Section id="features" testId="modules-section" className="bg-[#0B0C0E]">
    <Container>
      <FadeUp className="max-w-2xl">
        <Overline>Modules</Overline>
        <H2>Seven modules. One platform. Zero context switching.</H2>
        <Lead className="mt-4">Every module shares the same people, permissions and AI — so a complaint can become a task, and a task can show up in tomorrow's report.</Lead>
      </FadeUp>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
        {MODULES.map((m, i) => (
          <FadeUp key={m.title} delay={(i % 4) * 0.07} className={m.span || ''}>
            <div className="ld-card ld-card-lift p-6 sm:p-7 h-full flex flex-col" data-testid={`module-card-${i}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-[#E06D53]/10 border border-[#E06D53]/20 flex items-center justify-center"><m.icon className="w-5 h-5 text-[#E06D53]" /></div>
                <span className="ld-mono text-[10px] tracking-[0.2em] text-slate-600">0{i + 1}</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-100 mt-5">{m.title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">{m.desc}</p>
              <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-baseline gap-2">
                <span className="ld-mono text-xl font-semibold text-slate-100">{m.stat}</span>
                <span className="text-xs text-slate-500">{m.statLabel}</span>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </Container>
  </Section>
);

export const HowItWorks = () => (
  <Section id="how-it-works" testId="how-it-works-section">
    <Container>
      <FadeUp className="max-w-2xl">
        <Overline>How it works</Overline>
        <H2>Up and running in five minutes.</H2>
      </FadeUp>
      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {STEPS.map((s, i) => (
          <FadeUp key={s.n} delay={i * 0.12} className="relative">
            {i < STEPS.length - 1 && <div className="ld-step-line hidden md:block" />}
            <div className="w-14 h-14 rounded-2xl ld-glass flex items-center justify-center ld-mono text-[#E06D53] font-semibold" data-testid={`step-${i}`}>{s.n}</div>
            <h3 className="text-lg font-semibold text-slate-100 mt-6">{s.title}</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed max-w-xs">{s.text}</p>
          </FadeUp>
        ))}
      </div>
    </Container>
  </Section>
);
